/**
 * SUGERENCIAS DEL REGISTRO FITOSANITARIO MIENTRAS SE ESCRIBE EL NOMBRE.
 *
 * De las tres formas de dar de alta un producto —a mano, con una foto, con una
 * factura— solo la manual escribía a ciegas. Las otras dos pasan por scan-invoice,
 * que casa el nombre contra el registro del MAPA y rellena nº de registro y
 * categoría. Aquí se hace lo mismo, pero al revés: en vez de adivinar al final, se
 * ofrece al principio, y elige la persona.
 *
 * TODO ESTO ES SÍNCRONO Y PURO. La lista completa (~2.000 productos) la trae
 * `useRegistryCatalog` una vez por sesión; buscar es recorrer un array. Nada de
 * consultas por pulsación: el usuario está en un invernadero, no en una oficina.
 *
 * POR QUÉ NO SE REUTILIZA `matchRegistry` (_shared/registryLookup.ts): esa función
 * contesta a otra pregunta. La suya es «¿cuál es, con certeza, sin que nadie mire?»
 * y por eso devuelve null en cuanto hay dos candidatos — un nº de registro
 * equivocado en un cuaderno de campo es peor que un hueco. La de aquí es «¿cuáles
 * podrían ser?», con una persona delante que va a elegir: la ambigüedad no es un
 * fallo, es la lista. SWITCH y SWITCH ONE salen los dos, y se distinguen por su
 * composición.
 */

import { normalizeCore, normalizeRegistration } from "@/lib/productMatch";

/** Una fila del catálogo tal y como la sirve `phyto-registry-catalog`. */
export interface RegistryItem {
  /** Nº de registro tal cual: '22839' | 'ES-00521'. */
  num: string;
  nombre: string;
  /** Titular ya limpio de forma jurídica y ciudad ('Corteva Agriscience Spain'). */
  titular: string | null;
  /** Composición ('Spinosad 48% [SC]'): lo que separa SWITCH de SWITCH ONE. */
  formulado: string | null;
  vigente: boolean;
  /** Categoría de la app, o null (mojantes, fitorreguladores…). */
  category: string | null;
  /** `product_type` de la app, o null. */
  tipo: string | null;
}

/**
 * Mayúsculas, sin acentos y con la puntuación convertida en espacios.
 *
 * Deliberadamente MÁS SUAVE que `normalizeProductName`: aquélla se come envases y
 * unidades («5 L», «10X1»), que es lo correcto para emparejar una línea de factura
 * y desastroso para un buscador — quien teclea «spintor 120» vería desaparecer el
 * 120 justo cuando es lo que distingue el producto que busca.
 */
export function foldQuery(input: string | null | undefined): string {
  if (!input) return "";
  return String(input)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9%]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Longitud mínima para buscar «contenido en», no solo «empieza por». */
const CONTAINS_MIN = 2;

/** Cuántas caben sin convertir la ficha en una lista. */
export const SUGGEST_LIMIT = 6;

/**
 * Un producto con lo que cuesta calcular YA CALCULADO.
 *
 * Doblar el nombre y partirlo en palabras es barato una vez y caro dos mil veces
 * por letra: medido sobre las 1.998 filas reales, recalcularlo en cada pulsación
 * costaba 6,2 ms en un portátil — entre 25 y 40 en un teléfono de gama media, o
 * sea dos o tres fotogramas perdidos por cada tecla, justo mientras alguien
 * escribe. Lo que cambia al teclear es la CONSULTA, no el catálogo.
 */
interface Preparado {
  item: RegistryItem;
  /** `foldQuery(item.nombre)`. */
  name: string;
  /** `name` partido en palabras. */
  words: string[];
  /** `normalizeRegistration(item.num)`. */
  reg: string | null;
}

/**
 * La preparación se guarda contra el ARRAY que la pidió, no en una variable
 * suelta: react-query devuelve siempre la misma referencia mientras no vuelva a
 * traer el catálogo, así que se calcula una vez y se reutiliza en cada
 * pulsación; y cuando llega un catálogo nuevo, su array es otro y se vuelve a
 * preparar solo. Al ser débil, el índice del catálogo viejo se recoge con él.
 */
const preparados = new WeakMap<RegistryItem[], Preparado[]>();

function prepare(items: RegistryItem[]): Preparado[] {
  const guardado = preparados.get(items);
  if (guardado) return guardado;
  const hecho = items.map((item) => {
    const name = foldQuery(item.nombre);
    return { item, name, words: name.split(" ").filter(Boolean), reg: normalizeRegistration(item.num) };
  });
  preparados.set(items, hecho);
  return hecho;
}

/**
 * Los escalones, de más a menos evidente. El número es el orden, no una
 * puntuación: cualquier candidato de un escalón va antes que todos los del
 * siguiente.
 *
 *   0 · el nombre empieza por lo tecleado, o el nº de registro es ese exacto
 *   1 · alguna palabra del nombre empieza por lo tecleado ('cebo' → SPINTOR-CEBO)
 *   2 · el nombre lo contiene por dentro (desde dos letras)
 *   3 · todas las palabras tecleadas aparecen como principio de alguna, en
 *       cualquier orden ('oro quin' → ORO-QUIN)
 *
 * Con UNA sola letra solo valen 0 y 1. «S» son 169 de los 1.998 productos: pedir
 * además que la letra esté en el principio de una palabra es lo que convierte esa
 * lista en algo que se puede mirar.
 */
function tierOf(p: Preparado, q: string, terms: string[], regQuery: string | null): number | null {
  const { name, words } = p;

  if (regQuery && p.reg === regQuery) return 0;
  /* Con una sola palabra tecleada el 0 y el 1 dicen lo mismo (si el nombre
     empieza por ella, su primera palabra también). Se separan en cuanto lo
     tecleado lleva un espacio: «spintor 1» es principio del NOMBRE «SPINTOR 120
     SC», pero no es principio de ninguna de sus palabras sueltas. */
  if (name.startsWith(q)) return 0;
  if (words.some((w) => w.startsWith(q))) return 1;
  if (q.length < CONTAINS_MIN) return null;
  if (name.includes(q)) return 2;
  /* `terms.length > 1` es redundante —con un solo término esto sería el escalón
     1, que ya ha contestado antes— pero se deja escrito porque es lo que dice
     para qué está este escalón: para varias palabras en cualquier orden. */
  if (terms.length > 1 && terms.every((t) => words.some((w) => w.startsWith(t)))) return 3;
  return null;
}

/**
 * Lo tecleado como nº de registro, o null si no es un número.
 *
 * Solo se busca por número cuando lo tecleado ES un número: si no, «ES» de
 * «ESQUIVE» se tomaría por el prefijo de país de un nº de registro. Recibe la
 * consulta ya doblada (`foldQuery`).
 */
function regQueryOf(q: string): string | null {
  return /^[0-9]/.test(q) || /^ES[\s-]?\d/.test(q) ? normalizeRegistration(q) : null;
}

/**
 * Los candidatos, ya ordenados y recortados. Lista vacía = no hay nada que enseñar,
 * y eso es un caso NORMAL, no un error: de los 200 nombres distintos del inventario
 * real solo 43 empiezan por un nombre del registro. Lo demás son biológicos (los
 * ácaros y avispas no se registran como fitosanitarios), abonos, coadyuvantes y
 * materias activas sueltas («Abamectina 1.8%»). Quien llame a esto no debe decir
 * «sin resultados»: debe no enseñar nada.
 */
export function searchRegistry(
  items: RegistryItem[],
  query: string | null | undefined,
  limit: number = SUGGEST_LIMIT,
): RegistryItem[] {
  const q = foldQuery(query);
  if (!q) return [];

  const terms = q.split(" ").filter(Boolean);
  const regQuery = regQueryOf(q);

  const scored: Array<{ item: RegistryItem; tier: number }> = [];
  for (const p of prepare(items)) {
    const tier = tierOf(p, q, terms, regQuery);
    if (tier !== null) scored.push({ item: p.item, tier });
  }

  scored.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    // Un producto cancelado sigue siendo el número correcto del bidón que ya tienes
    // en la nave, así que no se esconde; pero nunca por delante de uno vigente.
    if (a.item.vigente !== b.item.vigente) return a.item.vigente ? -1 : 1;
    // Más corto primero: SWITCH antes que SWITCH ONE cuando se ha escrito «switch».
    if (a.item.nombre.length !== b.item.nombre.length) return a.item.nombre.length - b.item.nombre.length;
    return a.item.nombre.localeCompare(b.item.nombre, "es", { sensitivity: "base", numeric: true });
  });

  return scored.slice(0, Math.max(0, limit)).map((s) => s.item);
}

/** Los campos del formulario que rellena una sugerencia elegida. */
export interface RegistryPatch {
  product_name: string;
  registration_number: string;
  category?: string;
  product_type?: string;
}

/**
 * Qué se rellena al elegir una sugerencia.
 *
 * Nombre y número SIEMPRE: son los dos que justifican todo esto. Categoría y tipo
 * SOLO si el registro los sabe — 449 de los 1.998 productos no tienen categoría de
 * las nuestras (mojantes, fitorreguladores, encerados), y pisar con un valor
 * inventado lo que el usuario ya había elegido sería peor que dejarlo en paz.
 *
 * El nombre se sustituye por el OFICIAL a propósito, aunque quien escribía tuviera
 * el suyo. Es lo que hace que luego funcionen el detector de duplicados
 * (`findLikelyExisting`), la memoria global de categorías y el agrupado de los
 * informes: todos ellos cuelgan del nombre. Ninguno de los campos queda bloqueado.
 */
export function registryPatch(item: RegistryItem): RegistryPatch {
  const patch: RegistryPatch = {
    product_name: item.nombre,
    registration_number: item.num,
  };
  if (item.category) patch.category = item.category;
  if (item.tipo) patch.product_type = item.tipo;
  return patch;
}

/**
 * Lo que decide el alta cuando alguien pulsa «Continuar» SIN haber tocado la
 * lista. (Al guardar ya no se decide nada: ver `autocompletarDelRegistro` en
 * AddProductDialog.)
 *
 *  · `one`  — hay UNA ficha que es, sin que nadie tenga que mirar: se rellena sola.
 *  · `many` — hay candidatos, pero ninguno se puede dar por bueno: se pregunta.
 *  · null   — no hay nada en el registro que se parezca: se sigue a mano.
 */
export type RegistryAutoPick =
  | { kind: "one"; item: RegistryItem }
  | { kind: "many"; items: RegistryItem[] };

/**
 * ¿Hay UNA ficha del registro que sea, con certeza, la que se ha escrito?
 *
 * El caso que arregla (C1, 25-09-2026): se escribe «spintor 480 sc» entero, se
 * pulsa «Continuar» sin tocar la lista —el paso solo pide el nombre—
 * y el producto se guardaba sin nº, porque el campo del nº estaba escondido
 * mientras la lista seguía abierta. Elegir una sugerencia sí lo guardaba; no
 * elegirla lo perdía.
 *
 * NO BUSCA: trabaja sobre `matches`, lo que `searchRegistry` ya devolvió para
 * pintar la lista. Buscar otra vez serían las 1.998 filas recorridas dos veces
 * por pulsación (y el guardián `registroSinRedPorPulsacion` exige UNA sola
 * búsqueda en el alta).
 *
 * Solo cuenta el PRIMER escalón (el nombre empieza por lo tecleado, o el nº es
 * ese): «cebo» trae SPINTOR-CEBO por una palabra de dentro, y eso es una
 * pista, no una certeza. Dentro de él hace falta SIEMPRE un nombre EXACTO (o el
 * nº tecleado), y además una de dos:
 *
 *  1. es el ÚNICO del escalón (aunque esté cancelado: un bidón comprado antes
 *     de la cancelación lleva ese nº);
 *  2. es el único exacto, está vigente y es el único VIGENTE del escalón (los
 *     demás que lo alargan están cancelados).
 *
 * Un PRINCIPIO de nombre nunca basta, aunque solo haya una ficha que empiece
 * así (revisión del 25-09): «Oxicloruro de cobre» solo es principio de
 * OXICLORURO DE COBRE 50% P.M. (16412), pero en el MAPA hay 96 productos
 * vigentes con oxicloruro de cobre en la composición (25-09), y quien escribe
 * eso está nombrando la materia activa, no esa ficha. «Juvinal» → JUVINAL 10 EC, lo mismo. Eso se
 * pregunta, con la lista.
 *
 * Y tampoco basta con que el nombre sea idéntico: «Switch» es exactamente
 * SWITCH, pero también el principio de SWITCH ONE (vigente), y quien tiene
 * SWITCH ONE en la nave escribe «Switch». Un nº equivocado en un cuaderno de
 * campo es peor que un hueco (ver `registryLookup.ts`), así que eso se pregunta.
 */
export function pickUnambiguous(
  matches: RegistryItem[],
  name: string | null | undefined,
): RegistryAutoPick | null {
  const q = foldQuery(name);
  if (!q || matches.length === 0) return null;
  const regQuery = regQueryOf(q);

  const esElNumero = (m: RegistryItem) => regQuery !== null && normalizeRegistration(m.num) === regQuery;
  const primero = matches.filter((m) => esElNumero(m) || foldQuery(m.nombre).startsWith(q));
  const exactos = primero.filter((m) => esElNumero(m) || foldQuery(m.nombre) === q);

  if (primero.length === 1 && exactos.length === 1) return { kind: "one", item: primero[0] };

  /* Con `exactos` vacío, «todos los exactos son el vigente» sería verdad sin
     decir nada: por eso se exige que haya UNO y que sea él. */
  const vigentes = primero.filter((m) => m.vigente);
  if (exactos.length === 1 && exactos[0].vigente && vigentes.length === 1) {
    return { kind: "one", item: exactos[0] };
  }
  return { kind: "many", items: matches };
}

/**
 * «COMPLETAR DESDE EL REGISTRO» EN UN PRODUCTO QUE YA EXISTE (C1c, 26-09-2026).
 *
 * En la ficha del producto y en la foto de la etiqueta el nombre no es lo que
 * alguien teclea en el alta: es el de la factura o el envase, con tamaño,
 * fabricante y relleno («SPINTOR 480 SC (10X1) / 1 L. ECO (DOW) *», «Affirm
 * 5kg»). Buscado tal cual no casa con NADA, porque el buscador pide que el
 * nombre del registro empiece por lo escrito. Así que:
 *
 *  1. se le quita lo que no es nombre con `normalizeCore` (envase, unidades,
 *     palabras de relleno): «Affirm 5kg» → «AFFIRM»;
 *  2. si aún no sale nada, se quitan palabras POR EL FINAL —«SPINTOR 480 SC
 *     DOW» → «SPINTOR 480 SC»— hasta que algo case o quede una sola. Nunca por
 *     delante: la marca va primero, y quitarla es buscar otra cosa;
 *  3. sobre lo que salga decide `pickUnambiguous`, igual que el alta: una ficha
 *     que es → `one`; varias → `many` (la lista para elegir). «Switch 1» acaba
 *     en SWITCH y SWITCH ONE, y eso se pregunta.
 *
 * Se llama al TOCAR el chip, no al pintar: son hasta tantas búsquedas como
 * palabras tenga el nombre, y no se cobran en cada pulsación mientras alguien
 * corrige el nombre en la ficha.
 */
export function registryCandidatesFor(
  items: RegistryItem[],
  productName: string | null | undefined,
): RegistryAutoPick | null {
  const words = normalizeCore(productName).split(" ").filter(Boolean);
  for (let n = words.length; n >= 1; n--) {
    const q = words.slice(0, n).join(" ");
    const matches = searchRegistry(items, q);
    if (matches.length > 0) return pickUnambiguous(matches, q);
  }
  return null;
}

/**
 * Lo que rellena el autocompletado: lo mismo que una sugerencia elegida
 * (`registryPatch`), salvo la categoría y el tipo que la persona ya haya
 * tocado a mano. Elegir una fila es decir «es éste» y se lo lleva todo; que la
 * app acierte sola no le da derecho a pisar lo que alguien cambió.
 */
export function autoFillPatch(
  item: RegistryItem,
  touched: { category?: boolean; product_type?: boolean },
): RegistryPatch {
  const patch = registryPatch(item);
  if (touched.category) delete patch.category;
  if (touched.product_type) delete patch.product_type;
  return patch;
}
